import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';

export interface UploadedFile {
  filename: string;
  folder_path: string;
  nro: string;
  date: string;
  page: number;
}

export interface PayrollResponse {
  message: string;
  original_file: string;
  total_pages_processed: number;
  uploaded_files: UploadedFile[];
  uploaded_files_info: { folder_path: string; filename: string }[];
  failed_uploads: any[];
  statistics: {
    successful_uploads: number;
    failed_uploads: number;
    unique_folders: number;
    unique_nros: number;
    unique_dates: number;
    unknown_nros: number;
    unknown_dates: number;
  };
}

export interface ExtractNroResponse {
  message: string;
  extracted_data: any;
  file_name: string;
}

export interface GeneratePdfsResponse {
  message: string;
  generated_files?: string[];
}

export interface PayrollListResponse {
  files: string[];
  total: number;
  folder_path: string;
}

@Injectable({
  providedIn: 'root',
})
export class PayrollService {
  constructor(private apiService: ApiService) {}

  uploadPayroll(file: File): Observable<PayrollResponse> {
    const formData = new FormData();
    formData.append('filename', file);

    return this.apiService.buildAPIPost<PayrollResponse>(
      '/payrolls/upload/',
      formData as any
    );
  }

  extractNro(file: File, fileName?: string): Observable<ExtractNroResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_name', fileName || file.name);

    return this.apiService.buildAPIPost<ExtractNroResponse>(
      '/payrolls/extract-nro/',
      formData as any
    );
  }

  generatePdfs(file: File): Observable<GeneratePdfsResponse> {
    const formData = new FormData();
    formData.append('file', file);

    return this.apiService.buildAPIPost<GeneratePdfsResponse>(
      '/payrolls/generate-pdfs/',
      formData as any
    );
  }

  extractNroByMonthYear(
    month: number,
    year: number,
    company: 'serveto_svt' | 'serveto_xofers_lux' | 'serveto_gestio_lux' | 'serveto_sas'
  ): Observable<ExtractNroResponse> {
    const data = {
      month: month,
      year: year,
      company: company,
    };

    return this.apiService.buildAPIPost<ExtractNroResponse>(
      '/payrolls/extract-nro/',
      data as any
    );
  }

  listPayrollFiles(
    month: number,
    year: number
  ): Observable<PayrollListResponse> {
    const folderPath = `${year}/${month.toString().padStart(2, '0')}`;
    const data = {
      folder_path: folderPath,
    };

    return this.apiService.buildAPIPost<PayrollListResponse>(
      '/payrolls/list/',
      data as any
    );
  }

  checkPayrollFiles(month: number, year: number): Observable<any> {
    const data = {
      month: month,
      year: year,
    };

    return this.apiService.buildAPIPost<any>(
      '/payrolls/check-files/',
      data as any
    );
  }

  checkPayrollLatestCompany(company: string): Observable<any> {
    const data = {
      company: company,
    };
    return this.apiService.buildAPIGet<any>(
      `/payrolls/processes/?company=${company}`
    );
  }

  checkPayrollProcessStatus(processId: string): Observable<any> {
    return this.apiService.buildAPIGet<any>(
      `/payrolls/processes/${processId}/`
    );
  }

  getEmployeePayroll(year: number, month: number): Observable<any> {
    return this.apiService.buildAPIGet<any>(
      `/payrolls/employee-payrolls/?year=${year}&month=${month}`
    );
  }

  /**
   * Change status for multiple employee payrolls.
   * The backend expects a body like: { ids: [...], validate: true }
   */
  changeStatusEmployeePayroll(
    ids: number[],
    validate: boolean
  ): Observable<any> {
    const data = {
      ids: ids,
      validated: validate,
    };

    return this.apiService.buildAPIPost<any>(
      `/payrolls/employee-payrolls/`,
      data
    );
  }

  validateEmployeePayroll(id: number, data: any): Observable<any> {
    return this.apiService.buildAPIPost<any>(
      `/payrolls/employee-payrolls/${id}/`,
      data
    );
  }

  cancelPayroll(): Observable<any> {
    return this.apiService.buildAPIPost<any>(`/payrolls/processes/cancel-all/`);
  }

  getLatestProcesses(includePages: boolean = false): Observable<any> {
    const data = {
      include_pages: includePages,
    };

    return this.apiService.buildAPIPost<any>(
      '/payrolls/processes/latest/',
      data
    );
  }
}
